import api, { getMutationRequestConfig } from "../utils/axiosConfig";
import type {
    ValidationAnnotation,
    ValidationDataset,
    ValidationDatasetImportResult,
    ValidationDatasetListResponse,
    ValidationOptionsResponse,
    ValidationRule,
    ValidationRuleConfig,
    ValidationRuleListResponse,
} from "../types/modelValidation";

export const getValidationOptions =
    async (): Promise<ValidationOptionsResponse> => {
        const response = await api.get<ValidationOptionsResponse>(
            "/model-management/validation-options",
        );
        return response.data;
    };

export const listValidationDatasets = async (
    params: {
        keyword?: string;
        status?: string;
        limit?: number;
        offset?: number;
    } = {},
): Promise<ValidationDatasetListResponse> => {
    const response = await api.get<ValidationDatasetListResponse>(
        "/model-management/validation-datasets",
        { params },
    );
    return response.data;
};

export const getValidationDataset = async (
    stableId: string,
): Promise<ValidationDataset> => {
    const response = await api.get<ValidationDataset>(
        `/model-management/validation-datasets/${stableId}`,
    );
    return response.data;
};

export const createValidationDataset = async (payload: {
    name: string;
    description?: string;
}): Promise<ValidationDataset> => {
    const response = await api.post<ValidationDataset>(
        "/model-management/validation-datasets",
        payload,
    );
    return response.data;
};

export const updateValidationDataset = async (
    stableId: string,
    payload: { name: string; description?: string | null },
): Promise<ValidationDataset> => {
    const response = await api.patch<ValidationDataset>(
        `/model-management/validation-datasets/${stableId}`,
        payload,
    );
    return response.data;
};

export const deleteValidationDataset = async (
    stableId: string,
): Promise<void> => {
    await api.delete(
        `/model-management/validation-datasets/${stableId}`,
        getMutationRequestConfig(),
    );
};

export const addValidationHistorySamples = async (
    stableId: string,
    version: number,
    historyIds: string[],
    lockVersion: number,
): Promise<ValidationDataset> => {
    const response = await api.post<ValidationDataset>(
        `/model-management/validation-datasets/${stableId}/versions/${version}/history-samples`,
        { history_ids: historyIds, lock_version: lockVersion },
    );
    return response.data;
};

export const importValidationDatasetSamples = async (
    targetStableId: string,
    targetVersion: number,
    sourceStableId: string,
    lockVersion: number,
): Promise<ValidationDatasetImportResult> => {
    const response = await api.post<ValidationDatasetImportResult>(
        `/model-management/validation-datasets/${targetStableId}/versions/${targetVersion}/import-dataset-samples`,
        {
            source_dataset_stable_id: sourceStableId,
            lock_version: lockVersion,
        },
    );
    return response.data;
};

export const uploadValidationSample = async (
    stableId: string,
    version: number,
    file: File,
    lockVersion: number,
): Promise<ValidationDataset> => {
    const form = new FormData();
    form.append("file", file);
    form.append("lock_version", String(lockVersion));
    const response = await api.post<ValidationDataset>(
        `/model-management/validation-datasets/${stableId}/versions/${version}/upload`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
};

export const saveValidationAnnotations = async (
    stableId: string,
    version: number,
    sampleId: number,
    payload: {
        lockVersion: number;
        reviewed: boolean;
        annotations: Array<
            Pick<
                ValidationAnnotation,
                "annotation_id" | "display_category_key" | "bbox"
            >
        >;
    },
): Promise<ValidationDataset> => {
    const response = await api.put<ValidationDataset>(
        `/model-management/validation-datasets/${stableId}/versions/${version}/samples/${sampleId}/annotations`,
        {
            lock_version: payload.lockVersion,
            reviewed: payload.reviewed,
            annotations: payload.annotations,
        },
    );
    return response.data;
};

export const deleteValidationSample = async (
    stableId: string,
    version: number,
    sampleId: number,
    lockVersion: number,
): Promise<ValidationDataset> => {
    const response = await api.delete<ValidationDataset>(
        `/model-management/validation-datasets/${stableId}/versions/${version}/samples/${sampleId}`,
        { params: { lock_version: lockVersion } },
    );
    return response.data;
};

export const publishValidationDataset = async (
    stableId: string,
    version: number,
    lockVersion: number,
): Promise<ValidationDataset> => {
    const response = await api.post<ValidationDataset>(
        `/model-management/validation-datasets/${stableId}/versions/${version}/publish`,
        { lock_version: lockVersion },
    );
    return response.data;
};

export const reopenValidationDataset = async (
    stableId: string,
    version: number,
    lockVersion: number,
): Promise<ValidationDataset> => {
    const response = await api.post<ValidationDataset>(
        `/model-management/validation-datasets/${stableId}/versions/${version}/reopen`,
        { lock_version: lockVersion },
    );
    return response.data;
};

export const setValidationDatasetVersionDisabled = async (
    stableId: string,
    version: number,
    lockVersion: number,
    disabled: boolean,
): Promise<ValidationDataset> => {
    const response = await api.post<ValidationDataset>(
        `/model-management/validation-datasets/${stableId}/versions/${version}/${disabled ? "disable" : "restore"}`,
        { lock_version: lockVersion },
    );
    return response.data;
};

export const listValidationRules = async (
    params: {
        keyword?: string;
        status?: string;
        limit?: number;
        offset?: number;
    } = {},
): Promise<ValidationRuleListResponse> => {
    const response = await api.get<ValidationRuleListResponse>(
        "/model-management/validation-rules",
        { params },
    );
    return response.data;
};

export const getValidationRule = async (
    stableId: string,
): Promise<ValidationRule> => {
    const response = await api.get<ValidationRule>(
        `/model-management/validation-rules/${stableId}`,
    );
    return response.data;
};

export const createValidationRule = async (payload: {
    name: string;
    description?: string;
    config: ValidationRuleConfig;
}): Promise<ValidationRule> => {
    const response = await api.post<ValidationRule>(
        "/model-management/validation-rules",
        payload,
    );
    return response.data;
};

export const updateValidationRule = async (
    stableId: string,
    payload: {
        name: string;
        description?: string | null;
        lockVersion: number;
        config: ValidationRuleConfig;
    },
): Promise<ValidationRule> => {
    const response = await api.patch<ValidationRule>(
        `/model-management/validation-rules/${stableId}`,
        {
            name: payload.name,
            description: payload.description,
            lock_version: payload.lockVersion,
            config: payload.config,
        },
    );
    return response.data;
};

export const setValidationRuleVersionDisabled = async (
    stableId: string,
    version: number,
    lockVersion: number,
    disabled: boolean,
): Promise<ValidationRule> => {
    const response = await api.post<ValidationRule>(
        `/model-management/validation-rules/${stableId}/versions/${version}/${disabled ? "disable" : "restore"}`,
        { lock_version: lockVersion },
    );
    return response.data;
};
